import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { FaQrcode } from 'react-icons/fa';
import { OverlayPopover } from '../../components/OverlayPopover';
import i18n from '@/utils/i18n';
import { useLocation, useHistory } from 'react-router-dom';
import { Communicator } from '../../tools/Communication';
import { LayoutStoreInstance } from '../../LayoutStore';
import {
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import { AddSpecialUserModal } from './AddSpecialUserModal';
import { Dialog } from 'primereact/dialog';
import {
  LoadingContainer,
  PageContainer,
  PageHeader,
  ResponsiveTable,
} from '../../components/Layout';
import { FaStar, FaUpload } from 'react-icons/all';
import { IconButton } from '../../components/Button/Button';
import { Progress } from 'reactstrap';
import debounce from 'just-debounce-it';
import { download } from '@/utils/download';
import QRCodeStyling from 'qr-code-styling';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { ContentSection } from '../../components/Layout/ContentSection';
import AddUserWithClaimModal from './AddUserWithClaimModal';
import { getArrayBuffer } from '@/utils/bufferUtils';
import { Routes } from '@/utils/routes';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAddOfflineCardsToStockMutation } from '../../graphql/mutations/addOfflineCardsToStock.generated';
import { useImportOfflineCardsToStockMutation } from '../../graphql/mutations/importOfflineCardsToStock.generated';

interface IUser {
  id: string;
  userName: string;
  email: string;
  claims: string[];
  lastActionTimestamp: string;
}

interface UsersListPageProps {}

const UsersListPage: React.FC<UsersListPageProps> = () => {
  const location = useLocation();
  const history = useHistory();
  const urlParams = new URLSearchParams(location.search);

  useDocumentTitle('PageTitles:users');

  // State management
  const [users, setUsers] = useState<IUser[]>([]);
  const [claims, setClaims] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddSpecialUserModal, setShowAddSpecialUserModal] = useState(false);
  const [showCreateOfflineUsersModal, setShowCreateOfflineUsersModal] =
    useState(false);
  const [showCreateEventsUserModal, setShowCreateEventsUserModal] =
    useState(false);
  const [createOfflineUsersAmount, setCreateOfflineUsersAmount] = useState<
    number | null
  >(null);
  const [offlineUserCreationProgress, setOfflineUserCreationProgress] =
    useState<number>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState(urlParams.get('filter') || '');
  const [filterByClaimType, setFilterByClaimType] = useState('');
  const [listLimit, setListLimit] = useState(100);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [addOfflineCardsToStockMutation] = useAddOfflineCardsToStockMutation();
  const [importOfflineCardsToStockMutation] =
    useImportOfflineCardsToStockMutation();

  // Debounced reload function
  const reloadUsersDebounced = useCallback(
    debounce((filterValue: string) => {
      reloadUsers(10, filterValue);
    }, 300),
    [],
  );

  // Main data loading function
  const reloadUsers = useCallback(
    async (limit: number, filterValue: string, filterByClaimType = '') => {
      try {
        const response = await Communicator.Get('users/GetAllUsersWithFilter', {
          filter: encodeURIComponent(filterValue),
          filterByClaim: filterByClaimType,
          limit,
          offset: 0,
        });
        const data = await response.json();
        setUsers(data.users);
        setLoading(false);
        setIsLoadingMore(false);
      } catch (error) {
        console.error('Error loading users:', error);
        setLoading(false);
      }
    },
    [],
  );

  const loadClaims = useCallback(async () => {
    try {
      const response = await Communicator.Get('users/GetAllClaims', {});
      const data = await response.json();

      setClaims(data);
    } catch (error) {
      console.error('Error loading claims:', error);
      // @ts-ignore
      LayoutStoreInstance.setError(`Failed to load claims: ${error?.message}`);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadClaims();
    reloadUsers(listLimit, filter, filterByClaimType);
  }, []);

  // Filter change handler
  const changeFilter = useCallback(
    (newFilter: string) => {
      setFilter(newFilter);
      history.replace(`${Routes.users()}?filter=${newFilter}`);
      reloadUsersDebounced(newFilter);
    },
    [history, reloadUsersDebounced],
  );

  // Row click handler
  const handleRowClick = useCallback(
    (
      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
      userId: string,
    ) => {
      const target: any = event.target;
      if (target?.cellIndex !== 0) {
        history.push(Routes.users(userId));
      }
    },
    [history],
  );

  const renderUsersListPageTable = useCallback(
    (users: IUser[]) => {
      return (
        <div>
          <div className="input-group mb-2">
            <div className="input-group-text">{i18n.t('Common:search')}</div>
            <input
              type="text"
              className="form-control"
              value={filter}
              onChange={(e) => changeFilter(e.target.value)}
              placeholder={i18n.t('Users:searchPlaceholder')}
            />
          </div>
          <ResponsiveTable>
            <thead>
              <tr>
                <th>Id</th>
                <th>Username</th>
                <th>E-Mail</th>
                <th>Claims</th>
                <th>Last Action</th>
              </tr>
            </thead>
            <tbody>
              {users.length == 0 ? (
                <tr>
                  <td colSpan={5}>
                    {filter == ''
                      ? i18n.t('Users:noUsersFound')
                      : i18n.t('Users:noUserMatchesSearch')}
                  </td>
                </tr>
              ) : (
                users
                  .map((user) => (
                    <tr
                      key={user.id}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => handleRowClick(e, user.id)}
                    >
                      <td>
                        <a href={`/users/${user.id}`}>{user.id} </a>
                        <OverlayPopover
                          body={
                            <img
                              alt="QR code for user"
                              src={`/Users/QrCodeForUser/${user.id}/${false}`}
                            />
                          }
                          header={i18n.t('Users:qrCodeFor', {
                            userName: user.userName,
                          })}
                        >
                          <a style={{ cursor: 'pointer', color: '#76b852' }}>
                            <FaQrcode />
                          </a>
                        </OverlayPopover>
                      </td>
                      <td>
                        <p
                          style={{
                            width: '150px',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {user.userName}
                        </p>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        {user.claims.map((_) => (
                          <span
                            key={_}
                            style={{ whiteSpace: 'nowrap' }}
                            className="bg-primary me-1 mb-1 rounded px-2 py-1 text-white"
                          >
                            {i18n.t(`User:claim_${_}`)}
                          </span>
                        ))}
                      </td>
                      <td>
                        <p style={{ minWidth: '100px' }}>
                          {user.lastActionTimestamp.toDateString()}
                        </p>
                      </td>
                    </tr>
                  ))
                  .concat(
                    users.length == listLimit || isLoadingMore
                      ? [
                          <tr key="loadmore">
                            <td colSpan={5}>
                              <Button
                                className="btn-lg"
                                color="secondary"
                                disabled={isLoadingMore}
                                onClick={() => {
                                  reloadUsers(listLimit + 100, filter);
                                  setListLimit(listLimit + 100);
                                  setIsLoadingMore(true);
                                }}
                              >
                                Load more
                              </Button>
                            </td>
                          </tr>,
                        ]
                      : [],
                  )
              )}
            </tbody>
          </ResponsiveTable>
        </div>
      );
    },
    [users, filter, listLimit, isLoadingMore, handleRowClick, reloadUsers],
  );

  const renderOfflineUserModal = useCallback(() => {
    if (!showCreateOfflineUsersModal) {
      return null;
    }

    const hide = () => !isSubmitting && setShowCreateOfflineUsersModal(false);
    const execute = async () => {
      if (!createOfflineUsersAmount) {
        return;
      }

      try {
        // Creation goes through colugo, NOT vytal's /Users/GenerateOfflineUsers.
        // colugo still has vytal create the identity users behind the scenes —
        // only it can — but it also mints each card's STORE_ASSET TrackableItem
        // and books it into warehouse stock, in one transaction. A card created
        // through the vytal endpoint directly has no trackable and can never be
        // scanned onto a picking job.
        //
        // The CSV keeps vytal's exact shape (";"-separated, quoted, the id as
        // u + 32 hex without dashes) because it is what the card printer is fed.
        const outputLines: string[] = ['"IdWithPrefix";"SignupCode"\n'];

        setIsSubmitting(true);
        const maxUsersPerApiCall = 50;
        for (
          let usersRemaining = createOfflineUsersAmount;
          usersRemaining > 0;
          usersRemaining -= maxUsersPerApiCall
        ) {
          let amount = Math.min(maxUsersPerApiCall, usersRemaining);

          const creationResult = await addOfflineCardsToStockMutation({
            variables: { amount },
          });

          const cards = creationResult.data?.addOfflineCardsToStock;
          if (!cards || cards.length !== amount) {
            throw new Error(
              `Error while creating users: expected ${amount} cards, got ${cards?.length ?? 0}`,
            );
          }
          for (const card of cards) {
            const idWithPrefix = `u${card.user_id.replace(/-/g, '')}`;
            outputLines.push(`"${idWithPrefix}";"${card.signup_code}"\n`);
          }
          setOfflineUserCreationProgress(
            (100 * (createOfflineUsersAmount - usersRemaining)) /
              createOfflineUsersAmount,
          );
        }

        const blob = new Blob(
          [new Uint8Array([0xef, 0xbb, 0xbf]), ...outputLines, ''],
          {
            type: 'application/octet-stream',
          },
        );

        let a = window.document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = `OfflineUsers_${new Date()
          .toISOString()
          .replace(':', '.')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (error) {
        alert(`Error: ${error}`);
      } finally {
        setIsSubmitting(false);
        setOfflineUserCreationProgress(0);
        hide();
      }
    };

    const footer = (
      <div>
        <Button
          onClick={execute}
          disabled={!createOfflineUsersAmount || isSubmitting}
          color="danger"
          className="text-white"
        >
          {i18n.t('Users:generateOfflineUsersAndDownloadCsv')}
        </Button>
        <Button onClick={hide} disabled={isSubmitting} color="secondary">
          {i18n.t('Common:cancel')}
        </Button>
      </div>
    );

    return (
      <Dialog
        header={i18n.t('Users:generateOfflineUsers')}
        visible={showCreateOfflineUsersModal}
        onHide={hide}
        closable={!isSubmitting}
        style={{ minWidth: '50vw' }}
        footer={footer}
      >
        {isSubmitting ? (
          <div className="text-center">
            <Progress value={offlineUserCreationProgress || 0} max={100} />
            <div>{i18n.t('Users:processingPleaseWait')}</div>
            <div>
              <strong>{i18n.t('Users:thisCanTakeSeveralMinutes')}</strong>
            </div>
          </div>
        ) : (
          <Form>
            <FormGroup>
              <Label className="me-2" for="offlineUserCreationAmount">
                {i18n.t('Users:amountOfOfflineUsersToCreate')}:{' '}
              </Label>
              <Input
                id="offlineUserCreationAmount"
                name="offlineUserCreationAmount"
                type="number"
                max="10000"
                min="1"
                value={createOfflineUsersAmount || ''}
                onChange={(e) =>
                  setCreateOfflineUsersAmount(Number(e.target.value))
                }
              />
            </FormGroup>
          </Form>
        )}
      </Dialog>
    );
  }, [
    showCreateOfflineUsersModal,
    createOfflineUsersAmount,
    isSubmitting,
    setShowCreateOfflineUsersModal,
    setOfflineUserCreationProgress,
    addOfflineCardsToStockMutation,
  ]);

  // Import of a supplier-printed batch. Goes through colugo, NOT vytal's
  // /Users/CreateOfflineUsersFromCsv, so each imported card also gets the
  // STORE_ASSET trackable that makes it pickable — an import through the old
  // endpoint produced cards no picker could ever scan.
  //
  // The file is parsed here instead of being posted whole, because the mutation
  // takes rows rather than a blob. The accepted shape is unchanged from the C#
  // parser: one `u<uuid>,<signupCode>` per line, no header, comma separated.
  const onOfflineUsersCsvChanged = useCallback(
    async (event: any) => {
      if (event.target.files == 0) return;
      const file: File = event.target.files[0];

      try {
        const text = await file.text();
        const cards = text
          .split('\n')
          .filter((line) => line.trim() !== '')
          .map((line) => {
            const [idWithPrefix, signupCode] = line.split(',');
            return {
              user_id: (idWithPrefix ?? '').trim().replace(/^u/i, ''),
              signup_code: (signupCode ?? '').trim().toUpperCase(),
            };
          });

        if (cards.length === 0) {
          throw new Error('The file contains no rows');
        }

        await importOfflineCardsToStockMutation({ variables: { cards } });
        LayoutStoreInstance.setSuccess('Offline users created');
        reloadUsers(listLimit, filter);
      } catch (error) {
        console.log(error);
        LayoutStoreInstance.setError(i18n.t('Users:errorCreatingOfflineUsers'));
      } finally {
        // Let the same file be picked again after a failure.
        event.target.value = '';
      }
    },
    [listLimit, filter, reloadUsers, importOfflineCardsToStockMutation],
  );

  const getCsvUserCode = useCallback((id: string) => {
    return new QRCodeStyling({
      width: 230,
      data: id,
      height: 230,
      dotsOptions: {
        color: '#000000',
        type: 'square',
      },
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: 0,
        imageSize: 0,
        hideBackgroundDots: false,
      },
      backgroundOptions: {
        color: '#00000000',
      },
      qrOptions: {
        errorCorrectionLevel: 'Q',
      },
    });
  }, []);

  const onCateringUsersCsvChanged = useCallback(
    (event: any) => {
      if (event.target.files == 0) return;
      var file: File = event.target.files[0];

      Communicator.PostFileUpload('/Users/CreateCateringUsersFromCsv', file)
        .then(async (result) => {
          const blob = await result.blob();
          const csv = await blob.text();
          const rows = csv.split('\n');

          const parsedData = rows
            .filter((r) => !isNullOrWhitespace(r))
            .map((row) => {
              const values = row.split(';');
              if (values.length < 2) {
                throw Error('Invalid CSV');
              }
              const rowData = {
                externalId: values[0],
                vytalId: values.find((value) =>
                  value.startsWith('https://vytal.org?q=u'),
                ),
              };
              return rowData;
            });
          // Dynamically import ExcelJS to avoid error with vite
          const ExcelJS = await import('exceljs');
          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet('Main sheet');
          worksheet.properties.defaultRowHeight = 200;
          const promises = parsedData.map(async (row) => {
            const qrCode = getCsvUserCode(row.vytalId || '');
            const blob = await qrCode.getRawData('png');
            const buf = await getArrayBuffer(blob);
            return workbook.addImage({
              buffer: buf,
              extension: 'png',
            });
          });
          const images = await Promise.all(promises);
          worksheet.addRow(['external id', 'vytal id', 'qr code']);
          parsedData.forEach((row) =>
            worksheet.addRow([row.externalId, row.vytalId]),
          );
          worksheet.columns = [{ width: 35 }, { width: 40 }, { width: 32 }];
          images.forEach((i, index) =>
            worksheet.addImage(i, `C${index + 2}:C${index + 2}`),
          );
          workbook.xlsx.writeBuffer().then(function (buffer) {
            download(
              new Blob([buffer], { type: 'application/octet-stream' }),
              'cateringQrCodes.xlsx',
            );
          });
          LayoutStoreInstance.setSuccess('Offline users created');
          reloadUsers(listLimit, filter);
        })
        .catch((e) => {
          console.log(e);
          LayoutStoreInstance.setError(
            i18n.t('Users:errorCreatingOfflineUsers'),
          );
        });
    },
    [listLimit, filter, getCsvUserCode, reloadUsers],
  );

  return (
    <PageContainer>
      <ContentSection title={i18n.t('Users:users')} collapsable={false}>
        <PageHeader title={i18n.t('Users:users')}>
          <div>
            <IconButton
              className="me-2 mb-2"
              icon={<FaUpload />}
              label={i18n.t('Users:uploadOfflineUsersFile')}
              htmlFor="file-upload"
            />
            <input
              id="file-upload"
              style={{ display: 'none' }}
              type="file"
              onChange={onOfflineUsersCsvChanged}
            />
            <IconButton
              className="me-2 mb-2"
              icon={<FaUpload />}
              label={i18n.t('Users:uploadCateringUsersFile')}
              htmlFor="file-upload-catering"
            />
            <input
              id="file-upload-catering"
              style={{ display: 'none' }}
              type="file"
              onChange={onCateringUsersCsvChanged}
            />
          </div>

          <div>
            <IconButton
              className="me-2 mb-2"
              icon={<FaStar />}
              label={i18n.t('Users:createOfflineUsers')}
              onClick={() => setShowCreateOfflineUsersModal(true)}
            />
            <IconButton
              className="me-2 mb-2"
              icon={<FaStar />}
              label={i18n.t('Users:createSpecialUserAccount')}
              onClick={() => setShowAddSpecialUserModal(true)}
            />
            <IconButton
              className="me-2 mb-2"
              icon={<FaStar />}
              label={i18n.t('Users:createUserWithClaim')}
              onClick={() => setShowCreateEventsUserModal(true)}
            />
          </div>

          <br />
          <div>
            <InputGroup>
              <InputGroupText>Show only user with claim:</InputGroupText>
              <Input
                type="select"
                onChange={(event) => {
                  const claimType =
                    event.currentTarget.value !== 'all'
                      ? event.currentTarget.value
                      : '';

                  setFilterByClaimType(claimType);
                  reloadUsers(listLimit, filter, claimType);
                }}
              >
                <option value="all">{i18n.t('Common:all')}</option>
                {claims.map((claim) => {
                  return (
                    <option value={claim}>
                      {i18n.t(`User:claim_${claim}`)}
                    </option>
                  );
                })}
              </Input>
            </InputGroup>
          </div>
        </PageHeader>

        {loading && <LoadingContainer />}
        {!loading && renderUsersListPageTable(users)}

        {showAddSpecialUserModal && (
          <AddSpecialUserModal
            onClose={() => setShowAddSpecialUserModal(false)}
          />
        )}
        {renderOfflineUserModal()}

        {showCreateEventsUserModal && (
          <div>
            <AddUserWithClaimModal
              onSuccess={(userId) => {
                changeFilter(userId);
                setShowCreateEventsUserModal(false);
              }}
              onClose={() => {
                setShowCreateEventsUserModal(false);
              }}
            />
          </div>
        )}
      </ContentSection>
    </PageContainer>
  );
};

export const UsersListPageWithHistory = UsersListPage;
